import { Phone } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import React, { ReactNode } from "react";
import { Toggle } from "react-powerplug";
import { IGetGovernmentQuery } from "../../lib/queries/__generated__/GetGovernmentQuery.generated";
import Container from "../layout/Container";
import Dotdotdot from "../text/Dotdotdot";

interface IResourcesProps {
  government: NonNullable<IGetGovernmentQuery["government"]>;
}

export default function Resources({ government }: IResourcesProps) {
  const DEFAULT_PAGE_ITEMS = 6;
  const resources = government.resources || [];

  return (
    <Phone>
      {(isPhone: boolean) => (
        <Container>
          <div
            css={{
              backgroundColor: palette.white,
              borderRadius: 5,
              border: `solid 1px ${palette.lightestGray}`
            }}
          >
            <div
              css={{
                padding: 15,
                textTransform: "uppercase",
                fontSize: 12,
                color: palette.sealBlue,
                letterSpacing: 0.5,
                fontWeight: 500,
                borderBottom: `solid 1px ${palette.lightestGray}`
              }}
            >
              Resources ({resources.length})
            </div>

            <Toggle>
              {({ on: viewAll, toggle: toggleViewAll }) => (
                <>
                  <div
                    css={{
                      display: "grid",
                      gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr 1fr",
                      gridColumnGap: 16,
                      gridRowGap: 16,
                      padding: 16
                    }}
                  >
                    {resources.slice(0, viewAll ? resources.length : DEFAULT_PAGE_ITEMS).map((resource, i) => (
                      <Resource resource={resource} key={i} />
                    ))}
                  </div>

                  {resources.length > DEFAULT_PAGE_ITEMS && !viewAll && (
                    <div
                      role="button"
                      css={{
                        borderTop: `solid 1px ${palette.lightestGray}`,
                        padding: 15,
                        textAlign: "center",
                        fontSize: 12,
                        color: palette.sealBlue,
                        letterSpacing: 0.5,
                        fontWeight: 500,
                        cursor: "pointer"
                      }}
                      onClick={toggleViewAll}
                    >
                      VIEW ALL
                    </div>
                  )}
                </>
              )}
            </Toggle>
          </div>
        </Container>
      )}
    </Phone>
  );
}

interface ICardProps {
  children: ReactNode;
  href: string;
  download: string | null;
}
function Card({ children, href, download }: ICardProps) {
  return (
    <a
      css={{
        padding: 15,
        display: "flex",
        alignItems: "center",
        position: "relative",
        boxShadow: "0 3px 11px 0 rgba(0, 0, 0, 0.1)",
        textDecoration: "none",
        borderRadius: 10,
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.01)",
          boxShadow: "0 3px 11px 0 rgba(0, 0, 0, 0.15)"
        }
      }}
      href={href}
      download={download}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

interface IResourceProps {
  resource: NonNullable<NonNullable<IGetGovernmentQuery["government"]>["resources"]>[0];
}
function Resource({ resource }: IResourceProps) {
  if (resource.__typename === "DownloadResource") {
    return (
      <Card href={resource.url || ""} download={resource.filename}>
        <div
          css={{
            width: 24,
            height: 24,
            minWidth: 24,
            minHeight: 24
          }}
        >
          <DocumentIcon
            width={24}
            height={24}
            style={{
              minWidth: 24,
              minHeight: 24
            }}
          />
        </div>

        <div
          css={{
            marginLeft: 10,
            display: "flex",
            flexDirection: "column"
          }}
        >
          <span
            css={{
              fontSize: 14,
              color: palette.darkGray,

              fontWeight: 500,
              lineHeight: 1.2,
              "&:hover": {
                color: palette.darkGray
              }
            }}
          >
            <Dotdotdot clamp={2}>{resource.title}</Dotdotdot>
          </span>
        </div>
      </Card>
    );
  }

  if (resource.__typename === "LinkResource") {
    return (
      <Card href={resource.url || ""} download={null}>
        <LinkIcon
          width={24}
          height={24}
          style={{
            minWidth: 24,
            minHeight: 24
          }}
        />

        <div
          css={{
            marginLeft: 10,
            display: "flex",
            flexDirection: "column"
          }}
        >
          <span
            css={{
              fontSize: 14,
              color: palette.darkGray,
              fontWeight: 500,
              lineHeight: 1.2,
              "&:hover": {
                color: palette.darkGray
              }
            }}
          >
            <Dotdotdot clamp={2}>{resource.title}</Dotdotdot>
          </span>
        </div>
      </Card>
    );
  }

  return null;
}

function LinkIcon(props: React.SVGProps<any>) {
  return (
    <svg viewBox="0 0 482.8 482.8" width="1em" height="1em" {...props}>
      <path
        fill="#28D094"
        d="M255.2 209.3c-5.3 5.3-5.3 13.8 0 19.1 21.9 21.9 21.9 57.5 0 79.4l-115 115c-21.9 21.9-57.5 21.9-79.4 0l-17.3-17.3c-21.9-21.9-21.9-57.5 0-79.4l115-115c5.3-5.3 5.3-13.8 0-19.1s-13.8-5.3-19.1 0l-115 115C8.7 322.7 0 343.6 0 365.8c0 22.2 8.6 43.1 24.4 58.8l17.3 17.3c16.2 16.2 37.5 24.3 58.8 24.3s42.6-8.1 58.8-24.3l115-115c32.4-32.4 32.4-85.2 0-117.6-5.2-5.3-13.8-5.3-19.1 0z"
      />
      <path
        fill="#28D094"
        d="M458.5 58.2l-17.3-17.3c-32.4-32.4-85.2-32.4-117.6 0l-115 115c-32.4 32.4-32.4 85.2 0 117.6 5.3 5.3 13.8 5.3 19.1 0s5.3-13.8 0-19.1c-21.9-21.9-21.9-57.5 0-79.4l115-115c21.9-21.9 57.5-21.9 79.4 0l17.3 17.3c21.9 21.9 21.9 57.5 0 79.4l-115 115c-5.3 5.3-5.3 13.8 0 19.1 2.6 2.6 6.1 4 9.5 4s6.9-1.3 9.5-4l115-115c15.7-15.7 24.4-36.6 24.4-58.8 0-22.2-8.6-43.1-24.3-58.8z"
      />
    </svg>
  );
}

function DocumentIcon(props: React.SVGProps<any>) {
  return (
    <svg viewBox="0 0 16 22" width={16} height={22} {...props}>
      <g fill="#28D094" fillRule="nonzero">
        <path d="M12.643 10H3.357C3.143 10 3 9.8 3 9.5s.143-.5.357-.5h9.286c.214 0 .357.2.357.5s-.143.5-.357.5zM12.643 7H8.357C8.143 7 8 6.8 8 6.5s.143-.5.357-.5h4.286c.214 0 .357.2.357.5s-.143.5-.357.5z" />
        <path d="M15.636 22H.364C.145 22 0 21.853 0 21.633v-15.4c0-.073.073-.22.073-.293L5.89.073c.073 0 .218-.073.29-.073h9.455c.219 0 .364.147.364.367v21.266c0 .22-.145.367-.364.367zM.727 21.267h14.546V.733H6.327L.727 6.38v14.887z" />
        <path d="M6.611 7H.39C.156 7 0 6.844 0 6.611s.156-.389.389-.389h5.833V.39c0-.233.156-.389.39-.389C6.843 0 7 .156 7 .389V6.61C7 6.844 6.844 7 6.611 7zM12.643 13H3.357C3.143 13 3 12.8 3 12.5s.143-.5.357-.5h9.286c.214 0 .357.2.357.5s-.143.5-.357.5zM12.643 16H3.357C3.143 16 3 15.8 3 15.5s.143-.5.357-.5h9.286c.214 0 .357.2.357.5s-.143.5-.357.5zM12.643 19H3.357C3.143 19 3 18.8 3 18.5s.143-.5.357-.5h9.286c.214 0 .357.2.357.5s-.143.5-.357.5z" />
      </g>
    </svg>
  );
}
